<template>
  <div>
    <form v-scroll-to-error @submit.prevent="submitForm">
      <v-flex>
        <v-flex class="pb-16">
          <text-field
            v-model.trim="form.replyTo"
            :validator="$v.form.replyTo"
            :label="adminText('workflowsAll.reply_to_e_mail_a8e268a', 'Reply-to e-mail')"
          />
        </v-flex>
        <v-flex class="pb-16">
          <select-field
            v-model="form.planId"
            :validator="$v.form.planId"
            :items="workflowUi(plans)"
            :label="adminText('workflowsAll.membership_plans_23f59c2', 'Membership plans*')"
            item-select="id"
            item-render="name"
            @change="onPlanChange"
          />
        </v-flex>
        <template v-if="form.planId">
          <v-flex class="pb-16">
            <select-field
              v-model="form.recipientType"
              :validator="$v.form.recipientType"
              :items="workflowUi(recipients)"
              :disabled="recipients.length === 1"
              :label="adminText('workflowsAll.recipients_faf993d', 'Recipients*')"
              @change="onRecipientChange"
            />
          </v-flex>
          <v-flex class="pb-16">
            <v-autocomplete
              v-if="form.recipientType === 'Specific attendees'"
              ref="usersSelect"
              v-model="form.identifiers"
              v-click-outside="handleClickOutsideUsersSelect"
              :items="workflowUi(memberships)"
              item-value="id"
              item-text="name"
              :label="adminText('workflowsAll.people_b37554f', 'People')"
              multiple
              small-chips
              deletable-chips
            />
          </v-flex>
        </template>
        <v-flex class="pb-16">
          <text-field v-model.trim="form.subject" :validator="$v.form.subject" :label="adminText('workflowsAll.subject_8d183db', 'Subject')" />
        </v-flex>
        <v-flex class="pb-16">
          <text-editor-field
            v-model.trim="form.content"
            :validator="$v.form.content"
            :label="adminText('workflowsAll.content_4f9be05', 'Content')"
            height="390"
          />
        </v-flex>
      </v-flex>
      <div class="buttons-container">
        <main-button flat @click.prevent="goBack"> {{ adminText('workflowsAll.cancel_1507c40', 'CANCEL') }}</main-button>
        <div class="lower-buttons">
          <main-button depressed type="submit" :disabled="loading"> {{ adminText('workflowsAll.send_a2469c4', 'SEND') }}</main-button>
        </div>
      </div>
    </form>
    <div>
      <email-shortcodes :shortcodes="shortcodes" @shortcode="shortcodeAdded" />
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import {
  GET_ALL_MEMBERSHIP_USERS_BY_MEMBERSHIP_PLAN,
} from '@/graphql/membershipUser.graphql'
import { GET_SPORT_ORGANIZATION_MEMBERSHIP_PLANS } from '@/graphql/membershipPlan.graphql'
import { email, required } from 'vuelidate/lib/validators'
import EmailShortcodes from '@/components/emails/email-shortcodes.vue'
import { GET_ORGANIZATION } from '@/graphql/organization.graphql'
import { isEmpty } from 'lodash'

const EVERYONE = 'All attendees'
const SPECIFIC_PEOPLE = 'Specific attendees'

export default {
  mixins: [WorkflowDisplay],
  name: 'create-sport-membership-email-form',
  components: { EmailShortcodes },
  props: {
    eventTemplateId: {
      default: null,
      type: String,
    },
    organizationSlug: String,
    loading: Boolean,
    initialValues: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    const additionalInitialValues = !isEmpty(this.initialValues)
      ? {
        planId: this.initialValues.planId || null,
        recipientType: SPECIFIC_PEOPLE,
        identifiers: this.initialValues.attendeeIds || [],
      }
      : {}

    return {
      form: {
        planId: null,
        subject: '',
        content: '',
        replyTo: '',
        recipientType: null,
        identifiers: [],
        ...additionalInitialValues,
      },
      shortcodes: [
        'Full name',
        'First name',
        'Last name',
        'Email',
        'Plan name',
        'Qr code',
      ],
      recipients: [EVERYONE, SPECIFIC_PEOPLE],
      memberships: [],
      plans: [],
    }
  },
  async mounted() {
    if (!isEmpty(this.initialValues)) {
      await this.fetchMembershipUsers(this.initialValues.planId)
    }
  },
  validations() {
    return {
      form: {
        replyTo: { required, email },
        recipientType: { required },
        subject: { required },
        content: { required },
        planId: { required },
      },
    }
  },
  methods: {
    async submitForm() {
      this.$v.$touch()
      if(this.$v.$invalid) {
        return
      }
      this.$emit('onSubmit', this.form)
    },
    handleClickOutsideUsersSelect() {
      this.$refs['usersSelect'].isMenuActive = false
    },
    async shortcodeAdded(shortcode) {
      if (this.form.content) {
        this.form.content = this.form.content
          .slice(0, this.form.content.length - 4)
          .concat(` { ${shortcode} }`)
      } else {
        this.form.content = this.form.content.concat(`{ ${shortcode} }`)
      }
    },
    onRecipientChange(event) {
      if (event === EVERYONE) {
        this.form.identifiers = this.memberships.map(user => user.id)
      } else {
        this.form.identifiers = []
      }
    },
    async fetchMembershipUsers(planId) {
      const {
        data,
      } = await this.$apollo.query({
        query: GET_ALL_MEMBERSHIP_USERS_BY_MEMBERSHIP_PLAN,
        variables: {
          planId,
          organizationSlug: this.organizationSlug,
        },
      })

      this.memberships = data.getAllMembershipUsersByPlan
    },
    async onPlanChange(planId) {
      await this.fetchMembershipUsers(planId)
      this.form.identifiers = []
    },
  },
  apollo: {
    getOrganizationMembershipPlans: {
      query: GET_SPORT_ORGANIZATION_MEMBERSHIP_PLANS,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      manual: true,
      async result({ loading, data }) {
        if (!loading) {
          const { getOrganizationMembershipPlans } = data
          this.plans = getOrganizationMembershipPlans.map(plan => {
            return {
              id: plan.id,
              name: plan.name,
            }
          })
        }
      },
    },
    getOrganization: {
      query: GET_ORGANIZATION,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const {
            getOrganization: { email },
          } = data
          this.form.replyTo = email
        }
      },
    },

  },
}
</script>
